export const PAYPAL_PROCESSING_HASH = '#paypal-processing';
export const PAYMENT_SUCCESS_HASH = '#payment-success';
export const PAYMENT_FAILURE_HASH = '#payment-failure';
export const PAYMENT_FAILURE_HASHES = [
  '#payment-failture',
  PAYMENT_FAILURE_HASH,
] as const;

function getHashQueryString(rawHash: string): string {
  const hashValue = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
  if (!hashValue) {
    return '';
  }

  const questionMarkIndex = hashValue.indexOf('?');
  if (questionMarkIndex !== -1) {
    return hashValue.slice(questionMarkIndex + 1);
  }

  const ampersandIndex = hashValue.indexOf('&');
  if (ampersandIndex !== -1) {
    return hashValue.slice(ampersandIndex + 1);
  }

  return '';
}

export function getNormalizedPaymentHash(rawHash: string): string {
  if (!rawHash) {
    return '';
  }

  const normalizedHash = rawHash.trim().toLowerCase();
  if (!normalizedHash.startsWith('#')) {
    return '';
  }

  const hashValue = normalizedHash.slice(1).replace(/^\/+/, '');
  const hashBase = hashValue.split(/[?&/]/)[0];

  return hashBase ? `#${hashBase}` : '';
}

export function getPayPalReturnOrderId(url: URL): string | null {
  const searchToken = url.searchParams.get('token')?.trim();
  if (searchToken) {
    return searchToken;
  }

  // PayPal can append approval params inside the hash when return_url already
  // contains a fragment, so the browser may get #paypal-processing?token=... .
  const hashToken = new globalThis.URLSearchParams(getHashQueryString(url.hash))
    .get('token')
    ?.trim();

  return hashToken || null;
}

/** Key shared by deposit flow and {@link PaymentResultModal} (localStorage, cross-tab). */
export const PENDING_DEPOSIT_STORAGE_KEY = 'pendingDeposit';

/** Maps `cc-{id}` / `dodo-{id}` → amount (cents) so GA4 can resolve value when only transaction id is in the return hash. */
export const DEPOSIT_PURCHASE_AMOUNT_BY_INVOICE_KEY =
  'depositPurchaseAmountByInvoice';

/**
 * Fallback when pay URL did not yield an id: last crypto deposit amount, short TTL.
 */
export const LAST_CRYPTO_DEPOSIT_INTENT_KEY = 'lastCryptoDepositPurchaseIntent';

const DEPOSIT_TRANSACTION_ID_PATTERN = /^(cc|dodo)-\d+$/;
const CRYPTO_DEPOSIT_INTENT_MAX_AGE_MS = 45 * 60 * 1000;

function isDepositTransactionId(transactionId: string): boolean {
  return DEPOSIT_TRANSACTION_ID_PATTERN.test(
    transactionId.trim().toLowerCase(),
  );
}

type TCryptoDepositAmountHint = {
  valueInCents: number;
  currency: string;
  at: number;
};

function readAmountByInvoiceMap(): Record<string, TCryptoDepositAmountHint> {
  if (typeof window === 'undefined') {
    return {};
  }
  const raw = window.localStorage.getItem(
    DEPOSIT_PURCHASE_AMOUNT_BY_INVOICE_KEY,
  );
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {};
    }
    return parsed as Record<string, TCryptoDepositAmountHint>;
  } catch {
    return {};
  }
}

function writeAmountByInvoiceMap(
  map: Record<string, TCryptoDepositAmountHint>,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(
    DEPOSIT_PURCHASE_AMOUNT_BY_INVOICE_KEY,
    JSON.stringify(map),
  );
}

/** Called from deposit flow when we know `cc-{id}` or `dodo-{id}` before redirect. */
export function recordCryptoDepositAmountHintForTransactionId(
  transactionIdLowerCase: string,
  valueInCents: number,
  currency: string = 'USD',
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const key = transactionIdLowerCase.trim().toLowerCase();
  if (!isDepositTransactionId(key)) {
    return;
  }
  const map = readAmountByInvoiceMap();
  map[key] = {
    valueInCents,
    currency,
    at: Date.now(),
  };
  writeAmountByInvoiceMap(map);
}

/** Card deposit alias for analytics amount hints (`dodo-{id}`). */
export const recordCardDepositAmountHintForTransactionId =
  recordCryptoDepositAmountHintForTransactionId;

/** Called when pay URL did not expose an id to key hints; paired with return `invoice_id`. */
export function recordLastCryptoDepositIntent(
  valueInCents: number,
  currency: string = 'USD',
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const payload: TCryptoDepositAmountHint = {
    valueInCents,
    currency,
    at: Date.now(),
  };
  window.localStorage.setItem(
    LAST_CRYPTO_DEPOSIT_INTENT_KEY,
    JSON.stringify(payload),
  );
}

/** Card deposit alias when checkout URL did not expose `dodo-{id}`. */
export const recordLastCardDepositIntent = recordLastCryptoDepositIntent;

/** Removes snapshot so we do not double-use amounts after a pending-based purchase event. */
export function clearCryptoDepositAmountHintForTransactionId(
  transactionId: string,
): void {
  if (typeof window === 'undefined') {
    return;
  }
  const key = transactionId.trim().toLowerCase();
  if (!isDepositTransactionId(key)) {
    return;
  }
  const map = readAmountByInvoiceMap();
  if (!(key in map)) {
    return;
  }
  delete map[key];
  writeAmountByInvoiceMap(map);
}

/**
 * Resolves cents for a deposit return transaction id (e.g. `cc-42`, `dodo-42`):
 * prefers keyed hint, else a fresh "last intent" from the deposit page.
 */
export function tryConsumeCryptoDepositAmountForInvoiceReturn(
  invoiceIdLowerCase: string,
): { valueInCents: number; currency: string } | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const invoiceKey = invoiceIdLowerCase.trim().toLowerCase();
  if (!DEPOSIT_TRANSACTION_ID_PATTERN.test(invoiceKey)) {
    return null;
  }

  const map = readAmountByInvoiceMap();
  const keyed = map[invoiceKey];
  if (
    keyed &&
    typeof keyed.valueInCents === 'number' &&
    Number.isFinite(keyed.valueInCents)
  ) {
    delete map[invoiceKey];
    writeAmountByInvoiceMap(map);
    return {
      valueInCents: keyed.valueInCents,
      currency:
        typeof keyed.currency === 'string' && keyed.currency
          ? keyed.currency
          : 'USD',
    };
  }

  const lastRaw = window.localStorage.getItem(LAST_CRYPTO_DEPOSIT_INTENT_KEY);
  if (!lastRaw) {
    return null;
  }
  try {
    const last = JSON.parse(lastRaw) as TCryptoDepositAmountHint;
    if (
      typeof last.valueInCents !== 'number' ||
      !Number.isFinite(last.valueInCents) ||
      last.valueInCents <= 0
    ) {
      window.localStorage.removeItem(LAST_CRYPTO_DEPOSIT_INTENT_KEY);
      return null;
    }
    if (
      Date.now() - (typeof last.at === 'number' ? last.at : 0) >
      CRYPTO_DEPOSIT_INTENT_MAX_AGE_MS
    ) {
      window.localStorage.removeItem(LAST_CRYPTO_DEPOSIT_INTENT_KEY);
      return null;
    }
    window.localStorage.removeItem(LAST_CRYPTO_DEPOSIT_INTENT_KEY);
    return {
      valueInCents: last.valueInCents,
      currency:
        typeof last.currency === 'string' && last.currency
          ? last.currency
          : 'USD',
    };
  } catch {
    window.localStorage.removeItem(LAST_CRYPTO_DEPOSIT_INTENT_KEY);
    return null;
  }
}

/**
 * Reads deposit transaction id from the payment result hash when the provider
 * appends `invoice_id` (CryptoCloud) or `session_id` (Dodo) to the redirect.
 */
export function getCryptoInvoiceIdFromPaymentReturnHash(
  rawHash: string,
): string | null {
  const qs = getHashQueryString(rawHash);
  if (!qs) {
    return null;
  }

  const params = new globalThis.URLSearchParams(qs);
  const candidates = [
    params.get('invoice_id')?.trim(),
    params.get('session_id')?.trim(),
  ];

  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    const normalized = candidate.toLowerCase();
    if (DEPOSIT_TRANSACTION_ID_PATTERN.test(normalized)) {
      return normalized;
    }
  }

  return null;
}
